import React from "react";
import moment from "moment";

/**
 * Formats a currency/USD amount with commas and fixed decimals
 */
export const formatAmount = (amount, minDecimals = 2, maxDecimals = 3, customColor = null) => {
  if (amount === undefined || amount === null || amount === "" || isNaN(amount)) return "$00.00";
  const num = Number(amount);
  const isPositive = num >= 0;
  const absNum = Math.abs(num);
  const formattedNum = absNum.toLocaleString("en-US", {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
  });
  const prefix = absNum < 10 ? "0" : "";
  const formattedStr = `${isPositive ? "$" : "-$"}${prefix}${formattedNum}`;
  const textColor = customColor || (isPositive ? "#FFFFFF" : "#fb7474");

  return (
    <span style={{ color: textColor, fontWeight: 700 }}>
      {formattedStr}
    </span>
  );
};

/**
 * Formats a crypto amount with 2-3 fraction digits and leading 0 for numbers below 10
 */
export const formatCryptoAmount = (amount, minDecimals = 2, maxDecimals = 3) => {
  if (amount === undefined || amount === null || amount === "" || isNaN(amount)) return "00.00";
  const num = Number(amount);
  const absNum = Math.abs(num);
  const formatted = absNum.toLocaleString("en-US", {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
  });
  const prefix = absNum < 10 ? "0" : "";
  return num < 0 ? `-${prefix}${formatted}` : `${prefix}${formatted}`;
};

/**
 * Abbreviates long wallet addresses (e.g. 0x1234...5678)
 */
export const formatAddress = (address, start = 6, end = 5) => {
  if (!address) return "N/A";
  if (address.length <= start + end) return address;
  return `${address.slice(0, start)}...${address.slice(-end)}`;
};

/**
 * Abbreviates hashes or API keys (e.g. 0xabcd1234...5678)
 */
export const formatHash = (hash, start = 6, end = 5) => {
  if (!hash) return "N/A";
  if (hash.length <= start + end) return hash;
  return `${hash.slice(0, start)}...${hash.slice(-end)}`;
};

/**
 * Formats date/timestamp using moment
 */
export const formatDate = (dateStr, formatStr = "DD-MM-YYYY hh:mm A") => {
  if (!dateStr) return "N/A";
  return moment(dateStr).format(formatStr);
};

/**
 * Formats numeric values to fixed 2-3 decimal places with leading 0 for numbers below 10
 */
export const formatValue = (value, minDecimals = 2, maxDecimals = 3) => {
  if (value === undefined || value === null || value === "" || isNaN(value)) return "-";
  const num = Number(value);
  const absNum = Math.abs(num);
  const formatted = absNum.toLocaleString("en-US", {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
  });
  const prefix = absNum < 10 ? "0" : "";
  return num < 0 ? `-${prefix}${formatted}` : `${prefix}${formatted}`;
};

/**
 * Returns formatted status badge JSX with indicator dot
 */
export const formatStatusBadge = (status, styles = {}) => {
  if (!status) {
    return (
      <span className={`${styles.badgeStatus || ""} ${styles.badgeDefault || ""}`}>
        <span className={styles.statusDot || ""}></span> UNKNOWN
      </span>
    );
  }
  const s = String(status).toUpperCase();
  if (s.includes("SUCCESS") || s.includes("COMPLETED") || s.includes("DONE") || s.includes("APPROVED") || s.includes("CLEARED")) {
    return (
      <span className={`${styles.badgeStatus || ""} ${styles.badgeSuccess || ""}`}>
        <span className={styles.statusDot || ""}></span> {status}
      </span>
    );
  }
  if (s.includes("PENDING") || s.includes("PROCESSING")) {
    return (
      <span className={`${styles.badgeStatus || ""} ${styles.badgePending || ""}`}>
        <span className={styles.statusDot || ""}></span> {status}
      </span>
    );
  }
  if (s.includes("FAIL") || s.includes("REJECT")) {
    return (
      <span className={`${styles.badgeStatus || ""} ${styles.badgeFailed || ""}`}>
        <span className={styles.statusDot || ""}></span> {status}
      </span>
    );
  }
  return (
    <span className={`${styles.badgeStatus || ""} ${styles.badgeDefault || ""}`}>
      <span className={styles.statusDot || ""}></span> {status}
    </span>
  );
};

/**
 * Returns currency badge CSS class based on token name
 */
export const getCurrencyColor = (token, styles = {}) => {
  if (!token) return styles.badgeDefault || "";
  const t = String(token).toUpperCase();
  if (t.includes("USDT")) return styles.badgeUsdt || "";
  if (t.includes("BTC")) return styles.badgeBtc || "";
  if (t.includes("ETH")) return styles.badgeEth || "";
  if (t.includes("USD")) return styles.badgeUsd || "";
  return styles.badgeDefault || "";
};